<template>
  <div id="carrusel">
    <div
      class="carrusel-wrapper"
      :style="{
        width: `${(slides.length + 1) * 100}%`,
        transform: `translateX(-${current * slideWidth}px)`,
        transition: transitioning ? 'transform 0.5s ease' : 'none'
      }"
      ref="track"
    >
      <!-- Slides generados dinámicamente desde carruselSlides.js -->
      <!-- Se incluye una copia del primer slide al final para el loop infinito -->
      <div
        v-for="(slide, index) in slidesWithLoop"
        :key="index"
        class="banner-slide"
        :class="slide.className"
        :style="{ width: `${100 / (slides.length + 1)}%` }"
      >
        <div class="over">
          <div class="container">
            <div class="row g-0">
              <div class="col-xl-6 col-md-5 texto">
                <div>
                  <h1>
                    <strong>{{ slide.title }}</strong>
                  </h1>
                  <p v-if="slide.subtitle" class="subtitle">{{ slide.subtitle }}</p>
                  <p v-html="slide.description"></p>

                  <a
                    :href="slide.link"
                    class="btn btn-azul con-flecha"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ slide.buttonPrefix }} <strong>{{ slide.buttonStrong }}</strong>
                  </a>
                </div>
              </div>
              <div class="col-xl-6 col-md-7 imagen">
                <div class="over-imagen">
                  <img :src="getImage(slide.image)" :alt="slide.imageAlt" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Flechas de navegación -->
    <button class="carousel-arrow carousel-arrow-left" @click="prevSlide" aria-label="Slide anterior">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
      </svg>
    </button>
    <button class="carousel-arrow carousel-arrow-right" @click="nextSlide" aria-label="Slide siguiente">
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <!-- Paginador -->
    <div class="carousel-pagination">
      <button
        v-for="n in totalSlides"
        :key="n"
        class="pagination-dot"
        :class="{ active: currentSlide === n - 1 }"
        @click="goToSlide(n - 1)"
        :aria-label="`Ir al slide ${n}`"
      ></button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { carruselSlides } from '@/data/carruselSlides'

export default {
  name: 'CarruselBanner',
  setup() {
    // Datos de los slides (importados desde el archivo de configuración)
    const slides = carruselSlides
    const totalSlides = slides.length

    // Se duplica el primer slide al final para crear el efecto de loop infinito
    const slidesWithLoop = computed(() => [...slides, slides[0]])

    // Resuelve la ruta de la imagen desde src/assets/img/
    const getImage = (filename) => require(`@/assets/img/${filename}`)

    // Estado del carrusel
    const current = ref(0)
    const currentSlide = ref(0)
    const slideWidth = ref(0)
    const transitioning = ref(true)
    const track = ref(null)
    let resizeObserver = null
    let autoSlideInterval = null
    let handleResize = null

    // Función para recalcular el ancho de los slides
    const calculateSlideWidth = () => {
      if (track.value && track.value.children[0]) {
        slideWidth.value = track.value.children[0].offsetWidth
      }
    }

    // Función para avanzar al siguiente slide
    const nextSlide = () => {
      current.value++

      if (current.value < totalSlides) {
        currentSlide.value = current.value
      } else {
        currentSlide.value = 0
      }

      if (current.value === totalSlides) {
        setTimeout(() => {
          transitioning.value = false
          current.value = 0
          currentSlide.value = 0
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              transitioning.value = true
            })
          })
        }, 500)
      }
    }

    // Función para retroceder al slide anterior
    const prevSlide = () => {
      if (current.value === 0) {
        transitioning.value = false
        current.value = totalSlides
        currentSlide.value = totalSlides - 1

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            transitioning.value = true
          })
        })

        setTimeout(() => {
          current.value = totalSlides - 1
          currentSlide.value = totalSlides - 1
        }, 50)
      } else {
        current.value--
        currentSlide.value = current.value
      }
    }

    // Función para ir directamente a un slide específico
    const goToSlide = (index) => {
      if (index === currentSlide.value) return

      const diff = index - currentSlide.value

      if (current.value + diff === totalSlides) {
        current.value = totalSlides
        currentSlide.value = 0

        setTimeout(() => {
          transitioning.value = false
          current.value = 0
          currentSlide.value = 0
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              transitioning.value = true
            })
          })
        }, 500)
      } else if (current.value + diff < 0) {
        transitioning.value = false
        current.value = totalSlides
        currentSlide.value = totalSlides - 1

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            transitioning.value = true
          })
        })

        setTimeout(() => {
          current.value = index
          currentSlide.value = index
        }, 50)
      } else {
        current.value += diff
        currentSlide.value = index
      }
    }

    // Inicialización del carrusel
    onMounted(() => {
      // Calcula el ancho inicial
      calculateSlideWidth()

      // Recalcula el ancho cuando cambia el tamaño de la ventana
      handleResize = () => {
        calculateSlideWidth()
      }
      window.addEventListener('resize', handleResize)

      // Usa ResizeObserver para detectar cambios en el tamaño del contenedor
      if (window.ResizeObserver && track.value) {
        resizeObserver = new ResizeObserver(() => {
          calculateSlideWidth()
        })
        resizeObserver.observe(track.value)
      }

      // Intervalo que avanza el carrusel cada 5 segundos
      autoSlideInterval = setInterval(() => {
        nextSlide()
      }, 5000)
    })

    // Limpieza al desmontar
    onUnmounted(() => {
      if (handleResize) {
        window.removeEventListener('resize', handleResize)
      }
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      if (autoSlideInterval) {
        clearInterval(autoSlideInterval)
      }
    })

    return {
      slides,
      slidesWithLoop,
      current,
      currentSlide,
      totalSlides,
      slideWidth,
      transitioning,
      track,
      getImage,
      nextSlide,
      prevSlide,
      goToSlide
    }
  }
}
</script>

<style scoped>
/* ============================================
   CONTENEDOR PRINCIPAL DEL CARRUSEL
   ============================================ */
/* Contenedor principal: establece el área visible del carrusel */
#carrusel {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 595px;
}

/* ============================================
   WRAPPER DEL CARRUSEL (CONTENEDOR DE SLIDES)
   ============================================ */
/* Contenedor flexible que alberga todos los banners en fila.
   El ancho total se calcula dinámicamente en línea según la cantidad de slides */
.carrusel-wrapper {
  display: flex;
  height: 100%;
}

/* ============================================
   SLIDE INDIVIDUAL (BANNER)
   ============================================ */
/* Cada banner ocupa un porcentaje calculado dinámicamente en línea */
.banner-slide {
  flex-shrink: 0; /* Evita que se comprima */
  height: 100%;
  overflow: hidden;
}

/* ============================================
   BANNER RADIOGRAFÍA: ESTILOS ESPECÍFICOS
   ============================================ */
.banner-radiografia {
  /* Contenedor principal del banner */
  .over {
    height: 100%;
  }

  /* Contenedor Bootstrap */
  .container {
    height: 100%;
  }

  /* Fila Bootstrap */
  .row {
    height: 100%;
  }

  /* Sección de texto: misma tipografía y espaciado que el resto de banners */
  .texto {
    position: relative;
    z-index: 2;
    padding-top: 60px;

    h1 {
      font-size: 58px;
      line-height: 64px;
      width: 600px;
      margin-bottom: 55px;
    }

    /* Subtítulo: usa la misma tipografía que el párrafo descriptivo */
    .subtitle {
      margin-bottom: 35px;
      max-width: 600px;
      font-size: 20px;
      line-height: 28px;
    }

    p {
      margin-bottom: 35px;
      max-width: 600px;
      font-size: 20px;
      line-height: 28px;
    }
  }

  /* Contenedor de la imagen */
  .imagen {
    position: relative;
    z-index: 1;
    height: 100%;
  }

  /* Overlay de la imagen con gradientes */
  .over-imagen {
    display: flex;
    align-items: stretch;
    justify-content: center;
    height: 100%;
    position: relative;

    /* Gradiente horizontal */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 5%, rgba(255, 255, 255, 0.5) 18%, rgba(255, 255, 255, 0.2) 28%, transparent 38%, transparent 62%, rgba(255, 255, 255, 0.2) 72%, rgba(255, 255, 255, 0.5) 82%, rgba(255, 255, 255, 0.9) 95%, rgba(255, 255, 255, 1) 100%);
      z-index: 1;
      pointer-events: none;
    }

    /* Gradiente vertical */
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3) 0%, transparent 10%, transparent 90%, rgba(255, 255, 255, 0.3) 100%);
      z-index: 1;
      pointer-events: none;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
}

/* ============================================
   BANNER 1: ESTILOS ESPECÍFICOS
   ============================================ */
.banner-1 {
  /* Contenedor principal del banner */
  .over {
    height: 100%;
  }

  /* Contenedor Bootstrap */
  .container {
    height: 100%;
  }

  /* Fila Bootstrap */
  .row {
    height: 100%;
  }

  /* Sección de texto: posición y espaciado */
  .texto {
    position: relative;
    z-index: 2; /* Por encima de la imagen */
    padding-top: 60px; /* Espaciado superior para el texto */

    /* Título principal */
    h1 {
      font-size: 58px;
      line-height: 64px;
      width: 600px;
      margin-bottom: 55px;
    }

    /* Párrafo descriptivo */
    p {
      margin-bottom: 35px;
      max-width: 600px;
      font-size: 20px;
      line-height: 28px;
    }
  }

  /* Contenedor de la imagen */
  .imagen {
    position: relative;
    z-index: 1; /* Por debajo del texto */
    height: 100%;
  }

  /* Overlay de la imagen con gradientes */
  .over-imagen {
    display: flex;
    align-items: stretch;
    justify-content: center;
    height: 100%;
    position: relative;

    /* Gradiente horizontal: difumina los bordes izquierdo y derecho */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 5%, rgba(255, 255, 255, 0.5) 18%, rgba(255, 255, 255, 0.2) 28%, transparent 38%, transparent 62%, rgba(255, 255, 255, 0.2) 72%, rgba(255, 255, 255, 0.5) 82%, rgba(255, 255, 255, 0.9) 95%, rgba(255, 255, 255, 1) 100%);
      z-index: 1;
      pointer-events: none; /* Permite clics a través del overlay */
    }

    /* Gradiente vertical: difumina los bordes superior e inferior */
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3) 0%, transparent 10%, transparent 90%, rgba(255, 255, 255, 0.3) 100%);
      z-index: 1;
      pointer-events: none;
    }

    /* Imagen: cubre todo el espacio disponible */
    img {
      width: 100%;
      height: 100%;
      object-fit: cover; /* Mantiene proporción y cubre todo */
      object-position: center; /* Centra la imagen */
    }
  }
}

/* ============================================
   BANNER 2: ESTILOS ESPECÍFICOS
   ============================================ */
.banner-2 {
  /* Contenedor principal del banner */
  .over {
    height: 100%;
  }

  /* Contenedor Bootstrap */
  .container {
    height: 100%;
  }

  /* Fila Bootstrap */
  .row {
    height: 100%;
  }

  /* Sección de texto: posición y espaciado */
  .texto {
    position: relative;
    z-index: 2;
    padding-top: 60px; /* Más espacio superior que banner-1 */

    h1 {
      font-size: 58px;
      line-height: 64px;
      width: 600px;
      margin-bottom: 55px;
    }

    p {
      margin-bottom: 35px;
      max-width: 600px;
      font-size: 20px;
      line-height: 28px;
    }
  }

  /* Contenedor de la imagen */
  .imagen {
    position: relative;
    z-index: 1;
    height: 100%;
  }

  /* Overlay de la imagen con gradientes (igual que banner-1) */
  .over-imagen {
    display: flex;
    align-items: stretch;
    justify-content: center;
    height: 100%;
    position: relative;

    /* Gradiente horizontal */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 5%, rgba(255, 255, 255, 0.5) 18%, rgba(255, 255, 255, 0.2) 28%, transparent 38%, transparent 62%, rgba(255, 255, 255, 0.2) 72%, rgba(255, 255, 255, 0.5) 82%, rgba(255, 255, 255, 0.9) 95%, rgba(255, 255, 255, 1) 100%);
      z-index: 1;
      pointer-events: none;
    }

    /* Gradiente vertical */
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3) 0%, transparent 10%, transparent 90%, rgba(255, 255, 255, 0.3) 100%);
      z-index: 1;
      pointer-events: none;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
}

/* ============================================
   BANNER 3: ESTILOS ESPECÍFICOS
   ============================================ */
.banner-3 {
  /* Contenedor principal del banner */
  .over {
    height: 100%;
  }

  /* Contenedor Bootstrap */
  .container {
    height: 100%;
  }

  /* Fila Bootstrap */
  .row {
    height: 100%;
  }

  /* Sección de texto: posición y espaciado */
  .texto {
    position: relative;
    z-index: 2;
    padding-top: 120px; /* Mismo espaciado que banner-2 */

    h1 {
      font-size: 58px;
      line-height: 64px;
      width: 600px;
      margin-bottom: 55px;
    }

    p {
      margin-bottom: 35px;
      max-width: 600px;
      font-size: 20px;
      line-height: 28px;
    }
  }

  /* Contenedor de la imagen */
  .imagen {
    position: relative;
    z-index: 1;
    height: 100%;
  }

  /* Overlay de la imagen con gradientes (igual que banner-1 y banner-2) */
  .over-imagen {
    display: flex;
    align-items: stretch;
    justify-content: center;
    height: 100%;
    position: relative;

    /* Gradiente horizontal */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to right, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 5%, rgba(255, 255, 255, 0.5) 18%, rgba(255, 255, 255, 0.2) 28%, transparent 38%, transparent 62%, rgba(255, 255, 255, 0.2) 72%, rgba(255, 255, 255, 0.5) 82%, rgba(255, 255, 255, 0.9) 95%, rgba(255, 255, 255, 1) 100%);
      z-index: 1;
      pointer-events: none;
    }

    /* Gradiente vertical */
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3) 0%, transparent 10%, transparent 90%, rgba(255, 255, 255, 0.3) 100%);
      z-index: 1;
      pointer-events: none;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
}

/* ============================================
   MEDIA QUERIES: RESPONSIVE PARA TABLETS
   ============================================ */
@media (max-width: 1200px) {
  /* Banner Radiografía: misma tipografía que los otros banners en tablets */
  .banner-radiografia {
    .texto {
      h1 {
        font-size: 40px;
        line-height: 42px;
        margin-bottom: 20px;
      }

      .subtitle {
        margin-bottom: 20px;
        font-size: 20px;
        line-height: 24px;
      }

      p {
        margin-bottom: 20px;
        font-size: 20px;
        line-height: 24px;
      }
    }
  }

  /* Banner 1: Reduce tamaños de fuente en tablets */
  .banner-1 {
    .texto {
      h1 {
        font-size: 40px;
        line-height: 42px;
        margin-bottom: 20px;
      }

      p {
        margin-bottom: 20px;
        font-size: 20px;
        line-height: 24px;
      }
    }
  }

  /* Banner 2: Reduce tamaños de fuente en tablets */
  .banner-2 {
    .texto {
      h1 {
        font-size: 40px;
        line-height: 42px;
        margin-bottom: 20px;
      }

      p {
        margin-bottom: 20px;
        font-size: 20px;
        line-height: 24px;
      }
    }
  }

  /* Banner 3: Reduce tamaños de fuente en tablets */
  .banner-3 {
    .texto {
      h1 {
        font-size: 40px;
        line-height: 42px;
        margin-bottom: 20px;
      }

      p {
        margin-bottom: 20px;
        font-size: 20px;
        line-height: 24px;
      }
    }
  }
}

/* ============================================
   ESTILOS DEL BOTÓN
   ============================================ */
/* Botón morado con flecha naranja: estilo principal del carrusel */
#carrusel .btn.btn-azul.con-flecha {
  background-color: #3B1064; /* Morado oscuro de fondo */
  color: #fff; /* Texto blanco */
  border: 1px solid #3B1064; /* Borde morado */
  border-radius: 50px; /* Esquinas muy redondeadas (capsule-like) */
  padding: 12px 30px; /* Espaciado interno */
  font-size: 16px; /* Tamaño de fuente */
  font-weight: 500; /* Peso de fuente medio */
  text-decoration: none; /* Sin subrayado */
  display: inline-block; /* Permite padding y margin */
  transition: all 0.3s ease; /* Transición suave para hover */
  position: relative; /* Para posicionar la flecha */
  padding-right: 50px; /* Espacio extra a la derecha para la flecha */
}

/* Flecha naranja a la derecha del botón */
#carrusel .btn.btn-azul.con-flecha::after {
  content: '';
  position: absolute;
  right: 15px; /* Posición desde la derecha */
  top: 50%; /* Centrado verticalmente */
  transform: translateY(-50%); /* Ajuste fino del centrado */
  width: 0;
  height: 0;
  border-left: 8px solid #F05948; /* Color naranja de la flecha */
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
}

/* Efecto hover: cambia el fondo a naranja */
#carrusel .btn.btn-azul.con-flecha:hover {
  background-color: #F05948; /* Naranja al hacer hover */
  border-color: #F05948; /* Borde naranja al hacer hover */
  color: #fff; /* Mantiene texto blanco */
}

/* Estilo para el texto en negrita dentro del botón */
#carrusel .btn.btn-azul.con-flecha strong {
  font-weight: 700; /* Negrita más marcada */
}

/* ============================================
   FLECHAS DE NAVEGACIÓN
   ============================================ */
/* Flechas de navegación: botones circulares a los lados del carrusel */
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

/* Efecto hover: aumenta tamaño y opacidad */
.carousel-arrow:hover {
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transform: translateY(-50%) scale(1.1);
}

/* Efecto al hacer clic: reduce tamaño */
.carousel-arrow:active {
  transform: translateY(-50%) scale(0.95);
}

/* Icono SVG dentro de la flecha */
.carousel-arrow svg {
  width: 24px;
  height: 24px;
  color: #333;
}

/* Flecha izquierda: posición desde la izquierda */
.carousel-arrow-left {
  left: 20px;
}

/* Flecha derecha: posición desde la derecha */
.carousel-arrow-right {
  right: 20px;
}

/* ============================================
   PAGINADOR
   ============================================ */
/* Contenedor del paginador: barra con puntos indicadores */
.carousel-pagination {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  padding: 8px 16px;
  border-radius: 25px;
  backdrop-filter: blur(10px);
}

/* Punto individual del paginador */
.pagination-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
}

/* Efecto hover en el punto */
.pagination-dot:hover {
  background: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 1);
  transform: scale(1.2);
}

/* Punto activo: indica el slide actual */
.pagination-dot.active {
  background: rgba(255, 255, 255, 1);
  border-color: rgba(255, 255, 255, 1);
  transform: scale(1.3);
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.6);
}

/* ============================================
   MEDIA QUERIES: RESPONSIVE PARA MOBILE
   ============================================ */
/* Estilos mobile para evitar desbordamiento */
@media (max-width: 768px) {
  #carrusel {
    overflow-x: hidden;
    overflow-y: auto;
    width: 100%;
    max-width: 100%;
    height: auto;
    min-height: 400px;
  }

  .carousel-arrow {
    display: none;
  }

  .carrusel-wrapper {
    overflow: hidden;
  }

  .banner-slide {
    overflow: hidden;
  }

  .banner-radiografia,
  .banner-1,
  .banner-2,
  .banner-3 {
    .over {
      width: 100%;
      max-width: 100%;
      overflow: hidden;
    }

    .container {
      width: 100%;
      max-width: 100%;
      padding-left: 0;
      padding-right: 0;
      overflow: hidden;
    }

    .row {
      flex-direction: column;
      margin: 0;
      width: 100%;
      max-width: 100%;
      overflow: hidden;
    }

    .imagen {
      order: 1;
      height: auto;
      min-height: 250px;
      width: 100%;
      max-width: 100%;
      overflow: hidden;
      padding: 0;
    }

    .over-imagen {
      width: 100%;
      max-width: 100%;
      overflow: hidden;
    }

    .over-imagen img {
      width: 100%;
      max-width: 100%;
      height: auto;
      object-fit: cover;
    }

    .texto {
      order: 2;
      padding-top: 30px;
      padding-bottom: 30px;
      padding-left: 15px;
      padding-right: 15px;
      width: 100%;
      max-width: 100%;
      box-sizing: border-box;
      overflow-wrap: break-word;
      word-wrap: break-word;
    }

    .texto h1 {
      width: 100%;
      max-width: 100%;
      font-size: 28px;
      line-height: 32px;
      word-wrap: break-word;
      overflow-wrap: break-word;
      margin-left: 0;
      margin-right: 0;
    }

    .texto p {
      width: 100%;
      max-width: 100%;
      font-size: 16px;
      line-height: 22px;
      word-wrap: break-word;
      overflow-wrap: break-word;
      margin-left: 0;
      margin-right: 0;
    }
  }

  .carousel-pagination {
    bottom: 10px;
    padding: 6px 12px;
  }

  .carousel-counter {
    top: 10px;
    right: 10px;
    font-size: 12px;
    padding: 6px 12px;
  }
}
</style>
